import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const COMMANDS = {
  help: () => ({
    output: `Available commands:
  help     - Show this help message
  ls       - List directory contents
  cd       - Change directory
  pwd      - Print working directory
  whoami   - Display current user
  date     - Show current date and time
  clear    - Clear the terminal
  echo     - Display a line of text
  cat      - Display file contents
  uname    - Print system information
  uptime   - Show system uptime
  neofetch - Display system info (fancy)
  matrix   - Enter the matrix...
  about    - About this system`,
    type: "output",
  }),

  ls: (args) => {
    const dirs = {
      "/": ["bin", "etc", "home", "usr", "var", "tmp"],
      "/home": ["guest"],
      "/home/guest": ["readme.txt", ".bashrc"],
      "/etc": ["passwd", "hosts", "shadow"],
      "/bin": ["bash", "ls", "cat", "echo"],
    };
    const currentDir = args[0] || "/home/guest";
    const contents = dirs[currentDir] || dirs["/home/guest"];
    return {
      output: contents.join("  "),
      type: "output",
    };
  },

  cd: (args) => {
    if (!args[0] || args[0] === "~") {
      return { output: "", type: "output", newDir: "/home/guest" };
    }
    return { output: "", type: "output" };
  },

  pwd: () => ({
    output: "/home/guest",
    type: "output",
  }),

  whoami: () => ({
    output: "guest",
    type: "output",
  }),

  date: () => ({
    output: new Date().toString(),
    type: "output",
  }),

  echo: (args) => ({
    output: args.join(" "),
    type: "output",
  }),

  cat: (args) => {
    const files = {
      "readme.txt": `
=======================================
     Welcome to KMB.LOL Terminal
=======================================

This is a simulated Linux environment.
Feel free to explore with the available
commands. Type 'help' for a list.

> Connection established
> Session: guest@kmb.lol
> Status: SECURE

=======================================`,
      ".bashrc": "# ~/.bashrc\nexport PS1='guest@kmb:~$ '\nalias ll='ls -la'",
      "/etc/passwd": "root:x:0:0:root:/root:/bin/bash\nguest:x:1000:1000:Guest:/home/guest:/bin/bash",
      "/etc/hosts": "127.0.0.1 localhost\n::1 localhost\n192.168.1.1 kmb.lol",
    };
    const file = args[0] || "";
    if (files[file]) {
      return { output: files[file], type: "output" };
    }
    return { output: `cat: ${file}: No such file or directory`, type: "error" };
  },

  uname: (args) => {
    if (args[0] === "-a") {
      return {
        output: "KMB.OS 5.15.0-kmb #1 SMP PREEMPT x86_64 GNU/Linux",
        type: "output",
      };
    }
    return { output: "KMB.OS", type: "output" };
  },

  uptime: () => {
    const hours = Math.floor(Math.random() * 1000) + 100;
    return {
      output: ` ${new Date().toLocaleTimeString()} up ${hours} days, 13:37, 1 user, load average: 0.42, 0.33, 0.21`,
      type: "output",
    };
  },

  neofetch: () => ({
    output: `
        ██╗  ██╗███╗   ███╗██████╗     guest@kmb.lol
        ██║ ██╔╝████╗ ████║██╔══██╗    -------------
        █████╔╝ ██╔████╔██║██████╔╝    OS: KMB.OS 5.15
        ██╔═██╗ ██║╚██╔╝██║██╔══██╗    Host: Virtual Terminal
        ██║  ██╗██║ ╚═╝ ██║██████╔╝    Kernel: 5.15.0-kmb
        ╚═╝  ╚═╝╚═╝     ╚═╝╚═════╝     Shell: kmbsh 1.0
                                        Terminal: xterm-256color
        [ HACKER MODE ACTIVE ]          CPU: Quantum vCPU
                                        Memory: 16384 MB
`,
    type: "info",
  }),

  matrix: () => ({
    output: `
    Wake up, Neo...
    The Matrix has you...
    Follow the white rabbit.
    
    Knock, knock, Neo.
`,
    type: "info",
  }),

  about: () => ({
    output: `
╔═══════════════════════════════════════╗
║          KMB.LOL TERMINAL             ║
║     Hacker Futuristic Interface       ║
╠═══════════════════════════════════════╣
║  Version: 1.0.0                       ║
║  Status: ONLINE                       ║
║  Security: MAXIMUM                    ║
╚═══════════════════════════════════════╝`,
    type: "output",
  }),
};

export default function Terminal() {
  const [history, setHistory] = useState([
    {
      type: "info",
      content: `
╔═══════════════════════════════════════════════════════════╗
║                                                           ║
║     ██╗  ██╗███╗   ███╗██████╗    ██╗      ██████╗ ██╗    ║
║     ██║ ██╔╝████╗ ████║██╔══██╗   ██║     ██╔═══██╗██║    ║
║     █████╔╝ ██╔████╔██║██████╔╝   ██║     ██║   ██║██║    ║
║     ██╔═██╗ ██║╚██╔╝██║██╔══██╗   ██║     ██║   ██║██║    ║
║     ██║  ██╗██║ ╚═╝ ██║██████╔╝██╗███████╗╚██████╔╝███████╗
║     ╚═╝  ╚═╝╚═╝     ╚═╝╚═════╝ ╚═╝╚══════╝ ╚═════╝ ╚══════╝
║                                                           ║
║          [ SECURE TERMINAL v1.0 ]                         ║
║          Type 'help' for available commands               ║
║                                                           ║
╚═══════════════════════════════════════════════════════════╝
`,
    },
  ]);
  const [input, setInput] = useState("");
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const navigate = useNavigate();

  // Auto-scroll to bottom
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const executeCommand = useCallback(
    (cmd) => {
      const trimmed = cmd.trim();
      if (!trimmed) return;

      // Add command to display
      setHistory((prev) => [...prev, { type: "command", content: trimmed }]);

      // Add to command history
      setCommandHistory((prev) => [...prev, trimmed]);
      setHistoryIndex(-1);

      // Check for secret command
      if (trimmed === "/gamemode admin") {
        setHistory((prev) => [
          ...prev,
          {
            type: "info",
            content: `
[ ACCESS GRANTED ]
Redirecting to admin panel...
`,
          },
        ]);
        setTimeout(() => {
          navigate("/admin-5390214552274");
        }, 1000);
        return;
      }

      // Check for clear command
      if (trimmed === "clear") {
        setHistory([]);
        return;
      }

      // Parse command and args
      const parts = trimmed.split(" ");
      const command = parts[0].toLowerCase();
      const args = parts.slice(1);

      // Execute command
      if (COMMANDS[command]) {
        const result = COMMANDS[command](args);
        setHistory((prev) => [...prev, { type: result.type, content: result.output }]);
      } else {
        setHistory((prev) => [
          ...prev,
          {
            type: "error",
            content: `Command not found: ${command}. Type 'help' for available commands.`,
          },
        ]);
      }
    },
    [navigate]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      executeCommand(input);
      setInput("");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex =
          historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex] || "");
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex] || "");
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput("");
      }
    }
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  return (
    <div
      className="terminal-container grid-pattern"
      onClick={focusInput}
      data-testid="terminal-container"
    >
      {/* Terminal output */}
      <div className="terminal-output" ref={outputRef} data-testid="terminal-output">
        {history.map((item, index) => (
          <div
            key={index}
            className={`terminal-line animate-fade-in ${
              item.type === "command"
                ? "terminal-command"
                : item.type === "error"
                ? "terminal-error"
                : item.type === "info"
                ? "terminal-info"
                : "terminal-output-text"
            }`}
          >
            {item.type === "command" && (
              <span className="terminal-prompt">guest@kmb:~$ </span>
            )}
            <span style={{ whiteSpace: "pre-wrap" }}>{item.content}</span>
          </div>
        ))}
      </div>

      {/* Fixed input at bottom */}
      <div className="terminal-input-wrapper">
        <div className="flex items-center gap-2">
          <span className="terminal-prompt text-[#06b6d4] text-glow-sm whitespace-nowrap">
            guest@kmb:~$
          </span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="terminal-input flex-1 text-glow-sm"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            spellCheck="false"
            data-testid="terminal-input"
          />
          <span className="text-[#22c55e] cursor-blink">█</span>
        </div>
      </div>

      {/* Mobile focus button */}
      <button
        onClick={focusInput}
        className="fixed bottom-20 right-4 md:hidden w-12 h-12 bg-[#0a0a0a] border border-[#22c55e] text-[#22c55e] flex items-center justify-center text-xl z-50"
        aria-label="Focus terminal input"
        data-testid="focus-input-btn"
      >
        &gt;_
      </button>
    </div>
  );
}
